import { useAdsSheetQuery } from '../../api/queries';
import { formatNumber } from '../../lib/format';
import MetricCard from '../../components/MetricCard';
import SubTabLoading from './SubTabLoading';

type Props = { af: string };

// PMA's window normally trails ~a month behind, so only shout once the numbers are
// old enough to mislead someone reading them as current.
const STALE_AFTER_DAYS = 60;

const PPC_TICKET_FORM =
  'https://forms.zohopublic.com/jeremiahbrooker/form/PPCTicketingForm/formperma/TGlWhp7qhNebzIoxtcmI50XfJ2lVSOhpTt5PzZY9FDw';

function EmptyStateIcon() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      style={{ opacity: 0.3, marginBottom: 12 }}
    >
      <path d="M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

export default function AdsSubTab({ af }: Props) {
  const { data, isLoading, error } = useAdsSheetQuery(af);

  if (isLoading) {
    return (
      <SubTabLoading
        title="Loading Google Ads Data..."
        sub="Fetching metrics from Power My Analytics"
      />
    );
  }
  if (error) {
    return (
      <div className="tab-placeholder">
        <h3>Ads Data Unavailable</h3>
        <p>{(error as Error).message}</p>
      </div>
    );
  }
  // Flagged as running paid search, but nothing came through for the window PMA
  // reported on. Saying "no campaign" here is wrong and sends people to open a
  // ticket for a campaign they already have.
  if (!data?.location && data?.reason === 'no_data_in_window') {
    return (
      <div className="tab-placeholder">
        <EmptyStateIcon />
        <h3>No Google Ads Data for This Reporting Period</h3>
        <p>
          {af} is marked as running paid search, but Google Ads reported no activity
          {data.dateRange ? ` for ${data.dateRange.replace(/^Report between /, '')}` : ''}.
          That usually means the campaign launched after this window closed, or it
          isn't spending.
        </p>
        <p style={{ marginTop: 8 }}>
          To look into it,{' '}
          <a
            href="mailto:jake.keller@amadafranchise.com"
            style={{ color: '#2a5a8c', fontWeight: 600, textDecoration: 'underline' }}
          >
            email Jake Keller
          </a>
          , Amada's paid media specialist.
        </p>
      </div>
    );
  }
  if (!data?.location) {
    return (
      <div className="tab-placeholder">
        <EmptyStateIcon />
        <h3>No Active Google Ads Campaign</h3>
        <p>
          Please{' '}
          <a
            href={PPC_TICKET_FORM}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#2a5a8c', fontWeight: 600, textDecoration: 'underline' }}
          >
            click here
          </a>{' '}
          to begin a campaign.
        </p>
      </div>
    );
  }

  const loc = data.location;
  const period = (
    <span style={{ fontSize: 9, fontWeight: 400, letterSpacing: '0.3px' }}> · Last 90 Days</span>
  );
  const ageDays = data.windowAgeDays;
  const isStale = typeof ageDays === 'number' && ageDays > STALE_AFTER_DAYS;

  return (
    <>
      {isStale && (
        <div className="ads-stale-warning">
          <strong>These numbers are {ageDays} days old.</strong> The most recent Google
          Ads report for {af} covers{' '}
          {data.dateRange.replace(/^Report between /, '') || 'an earlier period'}, so
          they don't reflect current performance. Check that this franchisee is still
          included in the Power My Analytics report.
        </div>
      )}
      <div className="rankings-reviews-callout">
        <div className="rankings-reviews-callout-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 6-10 7L2 6" />
          </svg>
        </div>
        <div className="rankings-reviews-callout-text">
          For a more in-depth report (including highest performing zip codes and split
          bidding information),{' '}
          <a href="mailto:jake.keller@amadafranchise.com">click here</a>{' '}
          to email Jake Keller — Amada's paid media specialist.
        </div>
      </div>
      <div className="metrics-row-4">
        <MetricCard
          label={<>Clicks{period}</>}
          value={formatNumber(loc.clicks)}
          change={loc.clicksChange}
        />
        <MetricCard
          label={<>CTR{period}</>}
          value={`${loc.ctr.toFixed(2)}%`}
          change={loc.ctrChange}
        />
        <MetricCard
          label={<>Impressions{period}</>}
          value={formatNumber(loc.impressions)}
          change={loc.impressionsChange}
        />
        <MetricCard
          label={<>Conversions{period}</>}
          value={formatNumber(Math.round(loc.conversions))}
          change={loc.conversionsChange}
        />
      </div>

      <div className="metrics-row-4">
        <MetricCard
          label={<>Conv. Rate{period}</>}
          value={`${loc.convRate.toFixed(2)}%`}
          change={loc.convRateChange}
        />
        <MetricCard
          label={<>Cost / Conv{period}</>}
          value={`$${loc.costPerConv.toFixed(2)}`}
          change={loc.costPerConvChange}
          positiveIsGood={false}
        />
        <MetricCard
          label={<>Cost{period}</>}
          value={`$${formatNumber(Math.round(loc.cost))}`}
          change={loc.costChange}
          positiveIsGood={false}
        />
        <MetricCard
          label={<>Avg. CPC{period}</>}
          value={`$${loc.avgCPC.toFixed(2)}`}
          change={loc.avgCPCChange}
          positiveIsGood={false}
        />
      </div>

      {loc.campaigns && loc.campaigns.length > 0 && (
        <div className="ad-table">
          <div className="ad-table-header">Campaign Breakdown</div>
          <table>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Type</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loc.campaigns.map((c, i) => (
                <tr key={i}>
                  <td>{c.name}</td>
                  <td>{c.type}</td>
                  <td>{c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="metrics-footer">
        Google Ads — Last 90 Days • Data via Power My Analytics • {data.dateRange}
      </div>
    </>
  );
}
